import { clearGarageLocalStorage } from "@/lib/garageStorage";

export const PETROMIN_USER_COOKIE_NAME = "petromin_user";
const COOKIE_NAME = PETROMIN_USER_COOKIE_NAME;

/** Fired after {@link clearUserSessionOnLogout} — reset in-memory user state (e.g. garage). */
export const USER_SESSION_LOGOUT_EVENT = "petromin-user-session-logout";
/** Large / display-only; kept out of cookies to avoid HTTP 431 (header too large). */
const QR_STORAGE_KEY = "petromin_qr_code_url";
/** 30 days */
const MAX_AGE_SEC = 60 * 60 * 24 * 30;

/** Removed from cookies — cleared once per tab so existing users drop duplicate headers. */
const LEGACY_SCALAR_COOKIE_NAMES = [
  "petromin_customer_id",
  "petromin_tenant_id",
  "petromin_business_unit_id",
  "petromin_qr_code_url",
] as const;

const LEGACY_CLEANUP_FLAG = "petromin_legacy_auth_cookies_cleared";

export type AuthUser = {
  email?: string;
  phone: string;
  name?: string;
  customerId?: string;
  tenantId?: string;
  businessUnitId?: string;
  qrCodeUrl?: string;
};

type CookiePayload = Omit<AuthUser, "qrCodeUrl">;

function expireClientCookie(name: string): void {
  if (typeof document === "undefined") return;
  const secure =
    window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

function clearLegacyScalarCookiesIfNeeded(): void {
  if (typeof document === "undefined") return;
  try {
    if (sessionStorage.getItem(LEGACY_CLEANUP_FLAG) === "1") return;
    sessionStorage.setItem(LEGACY_CLEANUP_FLAG, "1");
  } catch {
    /* private mode — still attempt cleanup */
  }
  for (const name of LEGACY_SCALAR_COOKIE_NAMES) {
    expireClientCookie(name);
  }
}

function readQrFromStorage(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const v = localStorage.getItem(QR_STORAGE_KEY)?.trim();
    return v || undefined;
  } catch {
    return undefined;
  }
}

function writeQrToStorage(url: string | undefined): void {
  if (typeof window === "undefined") return;
  try {
    const trimmed = url?.trim();
    if (trimmed) localStorage.setItem(QR_STORAGE_KEY, trimmed);
    else localStorage.removeItem(QR_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

export function setAuthUserCookie(user: AuthUser): void {
  if (typeof document === "undefined") return;
  clearLegacyScalarCookiesIfNeeded();

  const cid = user.customerId?.trim();
  const payload: CookiePayload = {
    phone: user.phone,
    ...(user.name?.trim() ? { name: user.name.trim() } : {}),
    ...(user.email?.trim() ? { email: user.email.trim() } : {}),
    ...(cid ? { customerId: cid } : {}),
    ...(user.tenantId?.trim() ? { tenantId: user.tenantId.trim() } : {}),
    ...(user.businessUnitId?.trim()
      ? { businessUnitId: user.businessUnitId.trim() }
      : {}),
  };

  const value = encodeURIComponent(JSON.stringify(payload));
  document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${MAX_AGE_SEC}; SameSite=Lax`;

  writeQrToStorage(user.qrCodeUrl);
}

export function clearAuthUserCookie(): void {
  if (typeof document === "undefined") return;
  expireClientCookie(COOKIE_NAME);
  for (const name of LEGACY_SCALAR_COOKIE_NAMES) {
    expireClientCookie(name);
  }
  writeQrToStorage(undefined);
  try {
    sessionStorage.removeItem(LEGACY_CLEANUP_FLAG);
  } catch {
    /* ignore */
  }
}

/** Clears auth cookies, user localStorage (QR, garage), and notifies listeners. */
export function clearUserSessionOnLogout(): void {
  clearAuthUserCookie();
  clearGarageLocalStorage();
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(USER_SESSION_LOGOUT_EVENT));
  } catch {
    /* ignore */
  }
}

export function getAuthUserFromCookie(): AuthUser | null {
  if (typeof document === "undefined") return null;
  clearLegacyScalarCookiesIfNeeded();

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`)
  );
  if (!match?.[1]) return null;
  try {
    const raw = JSON.parse(decodeURIComponent(match[1])) as unknown;
    if (!raw || typeof raw !== "object" || !("phone" in raw)) return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.phone !== "string") return null;

    const customerIdRaw =
      (typeof o.customerId === "string" && o.customerId.trim()) ||
      (typeof o.customer_id === "string" && o.customer_id.trim()) ||
      "";
    const base: CookiePayload = {
      phone: o.phone,
      ...(typeof o.name === "string" && o.name.trim()
        ? { name: o.name.trim() }
        : {}),
      ...(typeof o.email === "string" && o.email.trim()
        ? { email: o.email.trim() }
        : {}),
      ...(customerIdRaw ? { customerId: customerIdRaw } : {}),
      ...(typeof o.tenantId === "string" && o.tenantId.trim()
        ? { tenantId: o.tenantId.trim() }
        : {}),
      ...(typeof o.businessUnitId === "string" && o.businessUnitId.trim()
        ? { businessUnitId: o.businessUnitId.trim() }
        : {}),
    };

    const legacyQr =
      typeof o.qrCodeUrl === "string" && o.qrCodeUrl.trim()
        ? o.qrCodeUrl.trim()
        : "";
    if (legacyQr) {
      setAuthUserCookie({ ...base, qrCodeUrl: legacyQr });
      return { ...base, qrCodeUrl: legacyQr };
    }

    const qr = readQrFromStorage();
    return qr ? { ...base, qrCodeUrl: qr } : base;
  } catch {
    /* ignore */
  }
  return null;
}
